/**
 * @file CommoraLogo.tsx
 * @description Логотип платформы Commora: знак + надпись «Commora» +
 * необязательная подпись (phase19.29).
 *
 * Знак — один файл `assets/brand/commora-mark.svg`, это ВРЕМЕННАЯ отрисовка
 * по картинке логотипа (OQ-111). Когда придёт исходник, заменяется только
 * файл: компонент и значок вкладки ссылаются на него по пути, правок кода
 * не нужно. Надпись — текстом (Inter 600, слегка отрицательный трекинг),
 * а не картинкой: читается на любом фоне и масштабе.
 *
 * Тон надписи — реестр `TONE_CLASS`: `dark` для светлого фона (вход),
 * `light` для тёмной боковой панели.
 *
 * ```tsx
 * <CommoraLogo tone="light" caption="Платформа" />
 * ```
 */

import commoraMarkUrl from "../../assets/brand/commora-mark.svg";
import { cn } from "@/lib/utils";

type LogoTone = "dark" | "light";
type LogoSize = "sm" | "md" | "lg" | "xl";

interface Props {
  /** Подпись под надписью: «Платформа», «Commerce Operating Platform». */
  caption?: string;
  tone?: LogoTone;
  size?: LogoSize;
  className?: string;
}

/** Цвета надписи и подписи по фону. */
const TONE_CLASS: Record<LogoTone, { word: string; caption: string }> = {
  dark: { word: "text-on-surface", caption: "text-on-surface-variant" },
  light: { word: "text-sidebar-strong", caption: "text-sidebar-foreground" },
};

/**
 * Размер знака (px) и кегль надписи (rem). Кегль — стилем, а не классом
 * шкалы: надпись — часть знака бренда, а не текст интерфейса.
 */
const SIZE_META: Record<LogoSize, { mark: number; wordRem: number }> = {
  sm: { mark: 24, wordRem: 1 },
  md: { mark: 28, wordRem: 1.125 },
  lg: { mark: 40, wordRem: 1.5 },
  xl: { mark: 56, wordRem: 2.25 },
};

export function CommoraLogo({ caption, tone = "dark", size = "md", className }: Props) {
  const toneClass = TONE_CLASS[tone];
  const sizeMeta = SIZE_META[size];
  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <img
        src={commoraMarkUrl}
        alt=""
        aria-hidden
        width={sizeMeta.mark}
        height={sizeMeta.mark}
        className="shrink-0"
      />
      <div className="flex min-w-0 flex-col">
        <span
          className={cn("font-semibold leading-tight tracking-[-0.03em]", toneClass.word)}
          style={{ fontSize: `${sizeMeta.wordRem}rem` }}
        >
          Commora
        </span>
        {caption ? (
          <span className={cn("truncate text-xs leading-tight", toneClass.caption)}>
            {caption}
          </span>
        ) : null}
      </div>
    </div>
  );
}
